import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useChatTabStore, type ChatTab } from "@/stores/chatTabStore";
import { PROVIDER_META, type AiProvider } from "@/types/ai-chat";

interface Props {
  projectId: number;
}

const MAX_TABS = 12;

function getStatusDot(tab: ChatTab) {
  if (tab.hasUnread) {
    return <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />;
  }
  switch (tab.status) {
    case "running":
      return <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />;
    case "error":
      return <span className="w-1.5 h-1.5 rounded-full bg-red-400" />;
    default:
      return <span className="w-1.5 h-1.5 rounded-full bg-colony-text-tertiary/60" />;
  }
}

const PROVIDERS = (Object.keys(PROVIDER_META) as AiProvider[]).map((id) => ({
  id,
  ...PROVIDER_META[id],
}));

export function ChatTabBar({ projectId }: Props) {
  const tabs = useChatTabStore((s) => s.tabs[projectId] ?? []);
  const activeTabId = useChatTabStore((s) => s.activeTabId[projectId]);
  const createTab = useChatTabStore((s) => s.createTab);
  const closeTab = useChatTabStore((s) => s.closeTab);
  const setActiveTab = useChatTabStore((s) => s.setActiveTab);

  const handleNewTab = (provider: AiProvider = "claude-code") => {
    if (tabs.length >= MAX_TABS) return;
    createTab(projectId, provider);
  };

  const handleClose = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    closeTab(projectId, tabId);
  };

  return (
    <div className="flex items-center h-9 px-1.5 bg-colony-bg border-b border-colony-border-subtle shrink-0">
      <div className="flex items-center gap-0.5 overflow-x-auto min-w-0 flex-1" style={{ scrollbarWidth: "none" }}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={cn(
              "group flex items-center gap-1.5 pl-2 pr-1.5 py-1 text-[11px] rounded-md transition-all duration-150",
              "select-none",
              tab.id === activeTabId
                ? "bg-white/[0.07] text-colony-text-primary"
                : "text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-white/[0.03]"
            )}
          >
            <button
              type="button"
              onClick={() => setActiveTab(projectId, tab.id)}
              className="flex items-center gap-1.5 min-w-0 rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-colony-text-tertiary"
            >
              <img src={PROVIDER_META[tab.provider].logo} alt={PROVIDER_META[tab.provider].label} className="w-3 h-3 rounded-sm object-contain opacity-60" />
              {getStatusDot(tab)}
              <span className="max-w-[120px] truncate">{tab.name}</span>
            </button>
            {tabs.length > 1 && (
              <button
                type="button"
                onClick={(e) => handleClose(tab.id, e)}
                aria-label="Close tab"
                className="ml-0.5 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-white/[0.08] text-colony-text-tertiary hover:text-colony-text-secondary transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}

        {tabs.length < MAX_TABS && (
          <button
            type="button"
            onClick={() => handleNewTab()}
            aria-label="New chat tab"
            className="flex items-center justify-center w-6 h-6 rounded text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-white/[0.04] transition-colors ml-0.5"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="w-px h-4 bg-colony-border-subtle mx-1.5" />

      {tabs.length < MAX_TABS && (
        <div className="flex items-center gap-0.5">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleNewTab(p.id)}
              className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-colony-text-tertiary rounded hover:bg-white/[0.05] hover:text-colony-text-secondary transition-colors"
              title={`New ${p.label} chat`}
            >
              <img src={p.logo} className="w-3.5 h-3.5 opacity-60" alt="" />
              <span>{p.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
